import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowLeft, Music2, Play, Pause, Share2, Link as LinkIcon } from "lucide-react";
import api from "@/lib/upr-api";

// Default cover shipped in /public — used when a playlist has no cover_image_url
// and no first-track fallback resolvable (kept absolute so social crawlers can
// fetch it without needing to know the site's canonical URL).
const DEFAULT_OG_IMAGE = "/og-image.jpg";

// Compact SVG icons for platforms Lucide doesn't ship (WhatsApp / X / Facebook /
// Instagram). Small, monochromatic, use `currentColor` so they inherit the theme.
const WhatsAppIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.7-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.7-1-2.3-.3-.6-.6-.5-.7-.5H7.6c-.2 0-.5.1-.7.4-.2.3-.9.9-.9 2.2s.9 2.6 1.1 2.7c.1.2 1.9 3.1 4.7 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.7.1.5-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.2-.3-.3-.6-.4Zm-5.4 7.3h0a9.1 9.1 0 0 1-4.7-1.3l-.3-.2-3.5.9.9-3.4-.2-.4A9.1 9.1 0 1 1 12 21.7Zm0-20A11 11 0 0 0 1 12.7c0 1.9.5 3.8 1.5 5.4L1 24l6-1.6a11 11 0 1 0 5-20.7Z" />
  </svg>
);
const XIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231ZM17.083 19.77h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);
const FacebookIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="currentColor" aria-hidden="true">
    <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8v-2.9h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3H15c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0 0 22 12Z" />
  </svg>
);
const InstagramIcon = (p) => (
  <svg viewBox="0 0 24 24" width={p.size || 16} height={p.size || 16} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37Z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

/**
 * Single interactive track list that IS the player — clicking any row loads
 * that track into the shared `<audio>` element and starts playing. The active
 * row shows a Pause icon and a "Playing" pill; every other row shows a Play
 * icon. Rows with no preview_url (title-only entries from the release's
 * tracklist) are dimmed and the click just selects them but audio.play() is a
 * no-op — so the UI stays honest.
 *
 * The same `setCurrent(i) + setPlaying(true)` logic is used across the site's
 * AudioPlayer, so the player context is consistent (see /components/AudioPlayer.jsx).
 */
function fmtTime(seconds) {
  if (!seconds || !isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function PlaylistPlayer({ tracks }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const t = tracks[current];
  const streamUrl = t?.preview_url || "";

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime);
    const onLoaded = () => setDuration(a.duration || 0);
    const onEnd = () => {
      // Auto-advance to the next playable track; stop at end of list.
      if (current < tracks.length - 1) setCurrent(current + 1);
      else setPlaying(false);
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
    };
  }, [current, tracks.length]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing, current]);

  const playTrack = (i) => {
    if (i === current) setPlaying((p) => !p);
    else { setCurrent(i); setPlaying(true); }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <div data-testid="playlist-player">
      <audio ref={audioRef} src={streamUrl} preload="metadata" />

      {/* Compact controls bar. */}
      <div
        className="flex items-center gap-4 p-3 mb-2"
        style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }}
      >
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          className="w-10 h-10 flex items-center justify-center transition-colors"
          style={{ background: "var(--upr-fg)", color: "var(--upr-bg)" }}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="playlist-player-toggle"
          disabled={!streamUrl}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-extrabold text-sm truncate">
            {t ? `${t.title} — ${t.artist_name}` : "—"}
          </div>
          <div className="text-xs" style={{ color: "var(--upr-muted)" }}>
            {fmtTime(progress)} / {fmtTime(duration)}
            {t && !streamUrl && <span className="ml-2 upr-eyebrow" style={{ fontSize: "0.55rem" }}>no audio</span>}
          </div>
        </div>
      </div>
      <div className="px-3 py-2 cursor-pointer" onClick={seek} style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)", borderTop: "none" }}>
        <div className="h-1 w-full" style={{ background: "var(--upr-border)" }}>
          <div className="h-1" style={{ width: `${duration ? (progress / duration) * 100 : 0}%`, background: "var(--upr-primary)" }} />
        </div>
      </div>

      {/* Merged track list — each row is BOTH visual metadata AND a play button. */}
      <ol className="mt-6" data-testid="playlist-track-list">
        {tracks.map((row, i) => {
          const isActive = i === current;
          const hasAudio = !!row.preview_url;
          return (
            <li
              key={`${row.release_id}-${row.track_index}-${i}`}
              className={"flex items-center gap-3 py-2 transition-colors " + (isActive ? "" : "hover:bg-[color:var(--upr-surface)]")}
              style={{
                borderBottom: "1px solid var(--upr-border)",
                background: isActive ? "var(--upr-surface)" : "transparent",
                opacity: hasAudio ? 1 : 0.62,
              }}
              data-testid={`playlist-track-${i}`}
            >
              <button
                type="button"
                onClick={() => playTrack(i)}
                className="w-10 h-10 shrink-0 flex items-center justify-center transition-colors"
                style={{
                  background: isActive ? "var(--upr-primary)" : "var(--upr-fg)",
                  color: "var(--upr-bg)",
                }}
                disabled={!hasAudio}
                aria-label={isActive && playing ? `Pause ${row.title}` : `Play ${row.title}`}
                data-testid={`playlist-track-${i}-play`}
              >
                {isActive && playing ? <Pause size={14} /> : <Play size={14} />}
              </button>

              <div className="w-14 h-14 shrink-0 overflow-hidden" style={{ border: "1px solid var(--upr-border)" }}>
                {row.cover_url && <img src={row.cover_url} alt="" className="w-full h-full object-cover" loading="lazy" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className={"font-heading font-extrabold truncate " + (isActive ? "text-[color:var(--upr-primary)]" : "")}>
                  {row.title}
                  {!hasAudio && <span className="ml-2 upr-eyebrow" style={{ fontSize: "0.55rem", color: "var(--upr-muted)" }}>no audio</span>}
                </div>
                <div className="text-xs truncate" style={{ color: "var(--upr-muted)" }}>
                  {row.artist_name}
                  {row.release_title && <> · <Link to={`/release/${row.release_id}`} className="upr-link-underline">{row.release_title}</Link></>}
                </div>
              </div>

              <span className="font-heading text-xs w-8 text-right shrink-0" style={{ color: "var(--upr-muted)" }}>
                {String(i + 1).padStart(2, "0")}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function ShareBlock({ playlist }) {
  const url = typeof window !== "undefined" ? window.location.href : `https://universopositivorecords.com/playlists/${playlist.slug}`;
  const text = `${playlist.title}${playlist.description ? " — " + playlist.description : ""} · Universo Positivo Records`;
  const enc = encodeURIComponent;

  const share = {
    whatsapp: `https://wa.me/?text=${enc(text + " " + url)}`,
    x:        `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste into your Instagram story or DM.");
    } catch {
      // Fallback for browsers without clipboard permission — surface the URL
      // in a prompt so the user can copy manually.
      window.prompt("Copy this link", url);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: playlist.title, text, url });
    } catch { /* user cancelled */ }
  };

  const hasNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  // Instagram doesn't expose a share-intent URL scheme (they only support
  // internal share sheet on their mobile app), so we treat it as a copy-link
  // action + a helpful toast. On mobile browsers that expose `navigator.share`,
  // we surface a native "Share…" button that includes Instagram in the sheet.
  return (
    <section className="mt-10 pt-6" style={{ borderTop: "1px solid var(--upr-border)" }} data-testid="playlist-share">
      <div className="upr-eyebrow mb-3">Share</div>
      <div className="flex flex-wrap gap-2">
        {hasNativeShare && (
          <button
            type="button"
            onClick={nativeShare}
            className="upr-btn"
            data-testid="share-native"
            title="Share via your device's share sheet"
          >
            <Share2 size={14} /> Share…
          </button>
        )}
        <a
          href={share.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className="upr-btn upr-btn-ghost"
          data-testid="share-whatsapp"
          title="Share on WhatsApp"
        >
          <WhatsAppIcon size={14} /> WhatsApp
        </a>
        <a
          href={share.x}
          target="_blank"
          rel="noopener noreferrer"
          className="upr-btn upr-btn-ghost"
          data-testid="share-x"
          title="Share on X"
        >
          <XIcon size={14} /> X
        </a>
        <a
          href={share.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="upr-btn upr-btn-ghost"
          data-testid="share-facebook"
          title="Share on Facebook"
        >
          <FacebookIcon size={14} /> Facebook
        </a>
        <button
          type="button"
          onClick={copyLink}
          className="upr-btn upr-btn-ghost"
          data-testid="share-instagram"
          title="Instagram doesn't support pre-filled links — copies the URL so you can paste into your story or DM"
        >
          <InstagramIcon size={14} /> Instagram
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="upr-btn upr-btn-ghost"
          data-testid="share-copy"
          title="Copy link"
        >
          <LinkIcon size={14} /> Copy link
        </button>
      </div>
    </section>
  );
}

export default function PlaylistDetail() {
  const { slug } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/playlists/${slug}`)
      .then(({ data }) => { if (!cancelled) setPlaylist(data); })
      .catch((e) => { if (!cancelled && e?.response?.status === 404) setNotFound(true); });
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="px-6 md:px-12 lg:px-16 py-16" data-testid="playlist-not-found">
        <h1 className="font-heading font-extrabold text-3xl mb-3">Playlist not found</h1>
        <Link to="/playlists" className="upr-link-underline text-sm">← Back to playlists</Link>
      </div>
    );
  }
  if (!playlist) return <div className="p-6 text-sm" style={{ color: "var(--upr-muted)" }}>Loading…</div>;

  // Absolute URL for OG:image — social crawlers need a full URL, not a relative
  // path. `window.location.origin` gives us the deployed host in production or
  // preview alike. Falls back to the site default OG image if the playlist has
  // no custom cover *and* no first-track artwork resolved.
  const origin = typeof window !== "undefined" ? window.location.origin : "https://universopositivorecords.com";
  const shareUrl = `${origin}/playlists/${playlist.slug}`;
  const ogImage = (playlist.cover_url && (playlist.cover_url.startsWith("http") ? playlist.cover_url : `${origin}${playlist.cover_url}`)) || `${origin}${DEFAULT_OG_IMAGE}`;
  const ogDescription = (playlist.description || "").trim() || "A curated selection from Universo Positivo Records.";

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="playlist-detail">
      <Helmet>
        <title>{`${playlist.title} · Playlist · Universo Positivo Records`}</title>
        <meta name="description" content={ogDescription} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="music.playlist" />
        <meta property="og:site_name" content="Universo Positivo Records" />
        <meta property="og:title" content={playlist.title} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={playlist.title} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <div className="mb-6">
        <Link to="/playlists" className="inline-flex items-center gap-2 upr-link-underline text-xs uppercase tracking-widest">
          <ArrowLeft size={12} /> All playlists
        </Link>
      </div>

      <div className="grid md:grid-cols-[minmax(0,320px)_1fr] gap-8 md:gap-12 items-start">
        <div
          className="aspect-square overflow-hidden"
          style={{ background: "var(--upr-surface)", border: "1px solid var(--upr-border)" }}
          data-testid="playlist-cover"
        >
          {playlist.cover_url ? (
            <img src={playlist.cover_url} alt={playlist.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: "var(--upr-muted)" }}>
              <Music2 size={60} />
            </div>
          )}
        </div>

        <div>
          <div className="upr-eyebrow mb-3">Playlist</div>
          <h1 className="font-heading font-black tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-4" style={{ letterSpacing: "-0.035em" }} data-testid="playlist-title">
            {playlist.title}
          </h1>
          {playlist.description && (
            <p className="text-base md:text-lg mb-5 max-w-xl" style={{ color: "var(--upr-muted)" }} data-testid="playlist-description">
              {playlist.description}
            </p>
          )}
          {playlist.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="playlist-tags">
              {playlist.tags.map((t) => (
                <Link
                  key={t}
                  to={`/?tag=${encodeURIComponent(String(t).toLowerCase())}`}
                  className="inline-flex items-center px-2 py-0.5 text-xs font-heading uppercase tracking-widest"
                  style={{ background: "var(--upr-fg)", color: "var(--upr-bg)" }}
                >
                  {t}
                </Link>
              ))}
            </div>
          )}
          <div className="text-xs" style={{ color: "var(--upr-muted)" }}>
            {playlist.tracks.length} {playlist.tracks.length === 1 ? "track" : "tracks"}
          </div>
        </div>
      </div>

      <div className="mt-12 max-w-3xl">
        {playlist.tracks.length === 0 ? (
          <div className="text-sm" style={{ color: "var(--upr-muted)" }} data-testid="playlist-empty">
            This playlist has no tracks yet.
          </div>
        ) : (
          <PlaylistPlayer tracks={playlist.tracks} />
        )}
      </div>

      <ShareBlock playlist={playlist} />
    </div>
  );
}
